const DEFAULT_MARKET = "nl";
const DEFAULT_CURRENCY = "EUR";
const MARKET_LOCALES = Object.freeze({
  nl: "nl-NL",
  de: "de-DE",
});

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function assertPattern(value, pattern, field) {
  if (typeof value !== "string" || !pattern.test(value)) {
    throw new Error(`Invalid Wallable market config field: ${field}`);
  }
  return value;
}

function parseBoolean(value, fallback = false) {
  if (value === true || value === "true" || value === "1") return true;
  if (value === false || value === "false" || value === "0") return false;
  return fallback;
}

function resolveUrl(value, origin, field) {
  try {
    const url = new URL(value, origin);
    if (!['http:', 'https:'].includes(url.protocol)) {
      throw new Error("Unsupported protocol");
    }
    return url.toString().replace(/\/$/, "");
  } catch {
    throw new Error(`Invalid Wallable market config URL: ${field}`);
  }
}

function getBrowserRuntime() {
  const browserWindow = typeof window === "undefined" ? undefined : window;
  return {
    browserWindow,
    origin: browserWindow?.location?.origin || "http://localhost",
    // Keep these as direct property reads: Parcel replaces them at build time.
    // Passing process.env as an object leaves an empty browser shim at runtime.
    environment: {
      WALLABLE_BUILD_MARKET: process.env.WALLABLE_BUILD_MARKET,
      WALLABLE_BUILD_ENV: process.env.WALLABLE_BUILD_ENV,
      WOOCOMMERCE_URL: process.env.WOOCOMMERCE_URL,
      USE_CART: process.env.USE_CART,
      OAK_FRAMES: process.env.OAK_FRAMES,
      ACRYL_PRODUCTS: process.env.ACRYL_PRODUCTS,
      ALUMINIUM_PRODUCTS: process.env.ALUMINIUM_PRODUCTS,
      TILE_PRODUCTS: process.env.TILE_PRODUCTS,
    },
  };
}

export function createMarketContext(config = {}, runtime = {}) {
  if (!isPlainObject(config)) {
    throw new Error("Wallable market config must be an object");
  }

  const services = isPlainObject(config.services) ? config.services : {};
  const configuredFeatures = isPlainObject(config.features) ? config.features : {};
  const catalog = isPlainObject(config.catalog) ? config.catalog : {};
  const environment = isPlainObject(runtime.environment)
    ? runtime.environment
    : {};
  const origin = resolveUrl(runtime.origin || "http://localhost", undefined, "origin");
  const woocommerceBaseUrl = resolveUrl(
    services.woocommerceBaseUrl || environment.WOOCOMMERCE_URL || origin,
    origin,
    "services.woocommerceBaseUrl",
  );

  const buildMarket = environment.WALLABLE_BUILD_MARKET;
  const market = assertPattern(
    buildMarket || config.market || DEFAULT_MARKET,
    /^[a-z]{2}$/,
    "market",
  );
  const locale = assertPattern(
    buildMarket ? MARKET_LOCALES[market] : config.locale || MARKET_LOCALES[market],
    /^[a-z]{2}-[A-Z]{2}$/,
    "locale",
  );
  const currency = assertPattern(
    config.currency || DEFAULT_CURRENCY,
    /^[A-Z]{3}$/,
    "currency",
  );

  return Object.freeze({
    market,
    locale,
    currency,
    catalog: Object.freeze(catalog),
    services: Object.freeze({
      woocommerceBaseUrl,
      routesEndpoint: resolveUrl(
        services.routesEndpoint ||
          `${market === "nl" && environment.WALLABLE_BUILD_ENV === "staging" ? "https://wallable.nl" : woocommerceBaseUrl}/wp-json/wallable/v1/routes`,
        origin,
        "services.routesEndpoint",
      ),
      routePlaceholderImage: resolveUrl(
        services.routePlaceholderImage ||
          `${woocommerceBaseUrl}/wp-content/uploads/woocommerce-placeholder.png`,
        origin,
        "services.routePlaceholderImage",
      ),
    }),
    features: Object.freeze({
      useCart: parseBoolean(configuredFeatures.useCart, parseBoolean(environment.USE_CART, true)),
      oakFrames: parseBoolean(
        configuredFeatures.oakFrames,
        parseBoolean(environment.OAK_FRAMES, true),
      ),
      acrylProducts: parseBoolean(
        configuredFeatures.acrylProducts,
        parseBoolean(environment.ACRYL_PRODUCTS, true),
      ),
      aluminiumProducts: parseBoolean(
        configuredFeatures.aluminiumProducts,
        parseBoolean(environment.ALUMINIUM_PRODUCTS, true),
      ),
      tileProducts: parseBoolean(
        configuredFeatures.tileProducts,
        parseBoolean(environment.TILE_PRODUCTS, true),
      ),
    }),
  });
}

export function getMarketContext() {
  const runtime = getBrowserRuntime();

  return createMarketContext(runtime.browserWindow?.WALLABLE_CONFIG || {}, {
    origin: runtime.origin,
    environment: runtime.environment,
  });
}

export async function loadMarketContext(options = {}) {
  const runtime = options.runtime || getBrowserRuntime();
  return createMarketContext(runtime.browserWindow?.WALLABLE_CONFIG || {}, runtime);
}
